/** QuickPlan — Nomenclature : pièces, débits, quincaillerie, lignes libres (éditables),
 *  plan de débit en barres, export CSV et impression. */
import { useMemo } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { computeBom, bomToCsv } from '../../shared/bom.js';
import { newId } from '../../shared/schema.js';
import NumField from './NumField.jsx';
import TextField from './TextField.jsx';
import ExportGlbButton from './ExportGlbButton.jsx';
import ExportStepButton from './ExportStepButton.jsx';
import { CAT_LABELS } from './LibraryPanel.jsx';

const money = (v) => (v == null ? '—' : v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const num = (v) => (v == null ? '' : v.toLocaleString('fr-FR', { maximumFractionDigits: 2 }));

export default function BomView() {
  const project = useProjectStore((s) => s.project);
  const library = useProjectStore((s) => s.library);
  const setSelection = useProjectStore((s) => s.setSelection);
  const setMode = useProjectStore((s) => s.setMode);
  const mutate = useProjectStore((s) => s.mutate);
  const bom = useMemo(() => computeBom(project, useProjectStore.getState().lookup()), [project, library]);

  const sections = [
    { title: 'Pièces et débits', rows: bom.rows.filter((r) => r.kind === 'piece' || r.kind === 'cut') },
    { title: 'Quincaillerie', rows: bom.rows.filter((r) => r.kind === 'kit') },
  ];
  const extras = bom.rows.filter((r) => r.kind === 'extra');

  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([bomToCsv(bom, project.name)], { type: 'text/csv;charset=utf-8' }));
    a.download = `${project.name.replace(/[^\w.-]+/g, '_') || 'projet'}-nomenclature.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const show = (ids) => { setSelection(ids); setMode('design'); };
  const setExtra = (id, patch) => mutate((p) => { Object.assign(p.extras.find((x) => x.id === id), patch); });

  return (
    <div className="bom">
      <div className="bom-head">
        <div>
          <h2>Nomenclature — {project.name}</h2>
          <div className="muted">
            {project.meta?.client && <>Client : {project.meta.client} · </>}
            {project.meta?.reference && <>Réf. {project.meta.reference} · </>}
            {new Date().toLocaleDateString('fr-FR')}
          </div>
        </div>
        <span className="grow" />
        <button onClick={exportCsv}>⤓ Export CSV (Excel)</button>
        <ExportGlbButton />
        <ExportStepButton />
        <button onClick={() => window.print()}>🖨 Imprimer</button>
      </div>

      {bom.errors.length > 0 && <div className="warn-box">{bom.errors.map((e) => <div key={e}>⚠ {e}</div>)}</div>}

      <table className="bom-table">
        <thead>
          <tr><th>Réf.</th><th>Désignation</th><th>Dimensions</th><th>Matière</th><th className="n">Qté</th><th className="n">P.U. €</th><th className="n">Total €</th><th className="noprint" /></tr>
        </thead>
        {sections.map((s) => s.rows.length > 0 && (
          <tbody key={s.title}>
            <tr className="section"><td colSpan={8}>{s.title}</td></tr>
            {s.rows.map((r) => (
              <tr key={`${r.kind}-${r.ref}-${r.label}-${r.length ?? ''}`}>
                <td className="mono">{r.ref}</td>
                <td>{r.name}{r.kind === 'cut' && <span className="tag">débit</span>}{r.category && r.kind !== 'kit' && <span className="cat"> · {CAT_LABELS[r.category]}</span>}</td>
                <td>{r.label}</td>
                <td className="muted">{r.material ?? ''}</td>
                <td className="n">{num(r.qty)}</td>
                <td className="n">{money(r.unitPrice)}</td>
                <td className="n">{money(r.total)}</td>
                <td className="noprint">{r.instances.length > 0 && <button className="icon" title="Sélectionner dans la vue 3D" onClick={() => show([...new Set(r.instances)])}>◎</button>}</td>
              </tr>
            ))}
          </tbody>
        ))}
        <tbody>
          <tr className="section"><td colSpan={8}>Lignes libres <button className="icon noprint" title="Ajouter une ligne" onClick={() => mutate((p) => { p.extras.push({ id: newId('x'), ref: '', name: 'Nouvelle ligne', qty: 1, unit: 'pce' }); })}>＋</button></td></tr>
          {extras.map((r) => (
            <tr key={r.id}>
              <td><TextField value={r.ref} max={60} onCommit={(v) => setExtra(r.id, { ref: v })} /></td>
              <td><TextField value={r.name} max={120} onCommit={(v) => v && setExtra(r.id, { name: v })} /></td>
              <td><TextField value={r.unit} max={10} onCommit={(v) => setExtra(r.id, { unit: v || 'pce' })} /></td>
              <td />
              <td className="n"><NumField value={r.qty} onCommit={(v) => v >= 0 && setExtra(r.id, { qty: v })} /></td>
              <td className="n"><NumField value={r.unitPrice} allowNull onCommit={(v) => setExtra(r.id, { price: v == null ? undefined : Math.max(0, v) })} /></td>
              <td className="n">{money(r.total)}</td>
              <td className="noprint"><button className="icon danger" onClick={() => mutate((p) => { p.extras = p.extras.filter((x) => x.id !== r.id); })}>✕</button></td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={6} className="n">Total HT{bom.unpriced ? ` (${bom.unpriced} ligne${bom.unpriced > 1 ? 's' : ''} sans prix)` : ''}</td><td className="n total">{money(bom.total)}</td><td className="noprint" /></tr>
        </tfoot>
      </table>

      {bom.cuts.length > 0 && (
        <>
          <h2>Plan de débit</h2>
          <p className="muted">Barres de {num(project.settings.barLength)} mm, trait de scie {num(project.settings.kerf)} mm — rangement « premier ajustement décroissant ».</p>
          {bom.cuts.map((c) => (
            <div key={c.stock} className="cut-block">
              <div className="cut-title">
                <strong>{c.stock}</strong> — {c.count} pièce{c.count > 1 ? 's' : ''}, {num(c.totalLength / 1000)} m, <strong>{c.bars.length} barre{c.bars.length > 1 ? 's' : ''}</strong>, rendement {num(c.usage)} %
                {c.oversize.length > 0 && <span className="warn"> · ⚠ plus longues qu'une barre : {c.oversize.join(', ')} mm</span>}
              </div>
              {c.bars.map((b, k) => (
                <div key={k} className="bar">
                  <span className="bar-n">{k + 1}</span>
                  <div className="bar-track">
                    {b.pieces.map((len, j) => (
                      <div key={j} className="bar-piece" style={{ width: `${(100 * len) / c.barLength}%` }} title={`${len} mm`}>{len}</div>
                    ))}
                  </div>
                  <span className="bar-off">chute {num(b.offcut)}</span>
                </div>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
